import { useState, useRef, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Divider,
    Alert,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import {
    Send as SendIcon,
    Description as WordIcon,
    HealthAndSafety as RiskIcon,
    Add as AddIcon,
    LibraryAdd as SaveLibraryIcon,
    LibraryBooks as LibraryIcon,
} from "@mui/icons-material";
import riskAssessmentToolService from "../../services/riskAssessmentToolService";
import {
    generateRiskAssessmentDoc,
    type RiskAssessment,
} from "../../services/generateRiskAssessmentDoc";
import { useNavigate } from "react-router-dom";
import AssessmentPreview from "./AssessmentPreview";

type ChatRole = "user" | "assistant";
interface ChatMessage {
    role: ChatRole;
    content: string;
    questions?: string[];
}

const useStyles = makeStyles()((theme) => ({
    root: { padding: theme.spacing(3) },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    headerTitle: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
    column: { height: "calc(100vh - 220px)", display: "flex", flexDirection: "column" },
    messages: {
        flexGrow: 1,
        overflowY: "auto",
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1.5),
    },
    bubbleAssistant: {
        alignSelf: "flex-start",
        background: "#F2EEFF",
        color: "#150850",
        borderRadius: "12px 12px 12px 2px",
        padding: theme.spacing(1.5),
        maxWidth: "85%",
    },
    bubbleUser: {
        alignSelf: "flex-end",
        background: "#7a5cf0",
        color: "#fff",
        borderRadius: "12px 12px 2px 12px",
        padding: theme.spacing(1.5),
        maxWidth: "85%",
    },
    inputBar: { display: "flex", gap: theme.spacing(1), padding: theme.spacing(2) },
    previewEmpty: {
        flexGrow: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#999",
        textAlign: "center",
        padding: theme.spacing(4),
    },
}));

const GREETING: ChatMessage = {
    role: "assistant",
    content:
        "Hi, I'm the WorkNest risk assessment assistant. Describe the activity, task or equipment you'd like a risk assessment for, and I'll ask a few questions then build it.",
};

const SESSION_KEY = "ra-generator-session";

interface PersistedSession {
    messages: ChatMessage[];
    assessment: RiskAssessment | null;
    conversationId: string | null;
    savedId: number | null;
    dirty: boolean;
}

// The chat lives in sessionStorage so navigating to the Library and back (or a refresh
// within the tab) keeps it; "New assessment" clears it.
const loadSession = (): Partial<PersistedSession> => {
    try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        return raw ? (JSON.parse(raw) as PersistedSession) : {};
    } catch {
        return {};
    }
};

const RiskAssessmentGenerator = () => {
    const { classes } = useStyles();
    const navigate = useNavigate();
    const [messages, setMessages] = useState<ChatMessage[]>(() => loadSession().messages ?? [GREETING]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [assessment, setAssessment] = useState<RiskAssessment | null>(() => loadSession().assessment ?? null);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [savedId, setSavedId] = useState<number | null>(() => loadSession().savedId ?? null);
    const [conversationId, setConversationId] = useState<string | null>(() => loadSession().conversationId ?? null);
    // True once an assessment has been generated/changed but not yet saved to the library.
    const [dirty, setDirty] = useState<boolean>(() => loadSession().dirty ?? false);
    const endRef = useRef<HTMLDivElement | null>(null);
    // Bumped on "New assessment" to invalidate any in-flight send, so a reply that
    // arrives after a reset can't land on the fresh chat.
    const sessionRef = useRef(0);

    useEffect(() => {
        if (endRef.current) {
            endRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    // Persist the session so navigating to the Library and back keeps the chat.
    useEffect(() => {
        try {
            sessionStorage.setItem(
                SESSION_KEY,
                JSON.stringify({ messages, assessment, conversationId, savedId, dirty }),
            );
        } catch {
            // storage unavailable/full — non-fatal, the chat just won't persist
        }
    }, [messages, assessment, conversationId, savedId, dirty]);

    const handleNewAssessment = () => {
        if (
            dirty &&
            !window.confirm(
                "You have an assessment that hasn't been saved to the Library. Start a new one and lose it?",
            )
        ) {
            return;
        }
        sessionRef.current += 1;
        setMessages([GREETING]);
        setInput("");
        setConversationId(null);
        setAssessment(null);
        setError(null);
        setNotice(null);
        setSaving(false);
        setSavedId(null);
        setSending(false);
        setGenerating(false);
        setDirty(false);
        try {
            sessionStorage.removeItem(SESSION_KEY);
        } catch {
            // ignore
        }
    };

    const handleSend = async () => {
        const text = input.trim();
        if (!text) return;
        // Snapshot the session so a reply that arrives after "New assessment" is discarded.
        const session = sessionRef.current;
        setMessages((m) => [...m, { role: "user", content: text }]);
        setInput("");
        setSending(true);
        setError(null);
        try {
            const data = await riskAssessmentToolService.sendMessage(conversationId, text);
            // The chat was reset while this request was in flight — drop the stale reply.
            if (session !== sessionRef.current) return;

            if (data && data.conversationId) {
                setConversationId(data.conversationId);
            }

            let parsed: any = null;
            try {
                parsed = data && data.content ? JSON.parse(data.content) : null;
            } catch (parseErr) {
                // Backend should always return the model's JSON; if it ever doesn't, surface
                // it for diagnosis rather than silently rendering "(no response)".
                console.error("Risk assessment: assistant response was not valid JSON", parseErr);
                parsed = null;
            }

            if (parsed && parsed.mode === "assessment" && parsed.assessment) {
                setAssessment(parsed.assessment as RiskAssessment);
                setDirty(true);
                setMessages((m) => [
                    ...m,
                    {
                        role: "assistant",
                        content:
                            parsed.message ||
                            "I've built your risk assessment — review it on the right and download the Word doc.",
                    },
                ]);
            } else if (parsed && ((parsed.questions && parsed.questions.length) || parsed.message)) {
                setMessages((m) => [
                    ...m,
                    { role: "assistant", content: parsed.message || "", questions: parsed.questions || [] },
                ]);
            } else {
                setMessages((m) => [
                    ...m,
                    { role: "assistant", content: (data && data.content) || "(no response)" },
                ]);
            }
        } catch (e: any) {
            if (session !== sessionRef.current) return;
            setError(typeof e === "string" ? e : e?.message || "Something went wrong talking to the assistant.");
        } finally {
            // Only clear the spinner if this is still the active session, so a stale
            // request can't reset the state of a freshly-started one.
            if (session === sessionRef.current) {
                setSending(false);
            }
        }
    };

    const handleDownload = async () => {
        if (!assessment) return;
        setError(null);
        setGenerating(true);
        try {
            await generateRiskAssessmentDoc(assessment);
        } catch (e: any) {
            setError(e?.message || "Failed to generate the Word document.");
        } finally {
            setGenerating(false);
        }
    };

    const handleSaveToLibrary = async () => {
        if (!assessment) return;
        setError(null);
        setSaving(true);
        try {
            const result = await riskAssessmentToolService.saveToLibrary({
                id: savedId,
                title: assessment.title || "Risk assessment",
                description: assessment.description || "",
                assessmentJson: JSON.stringify(assessment),
            });
            // Remember the id so re-saving this assessment updates the same row (keeps the library tidy).
            setSavedId(result.id);
            setDirty(false);
            setNotice(savedId ? "Updated in the Library." : "Saved to the Library.");
        } catch (e: any) {
            setError(typeof e === "string" ? e : e?.message || "Couldn't save to the Library.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box className={classes.root}>
            <Box className={classes.header}>
                <Box className={classes.headerTitle}>
                    <RiskIcon style={{ color: "#7a5cf0", fontSize: 32 }} />
                    <Typography variant="h4">Risk Assessment Generator</Typography>
                </Box>
                <Box display="flex" gap={1}>
                    <Button variant="outlined" startIcon={<LibraryIcon />} onClick={() => navigate("/risk-assessment-library")}>
                        Library
                    </Button>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={handleNewAssessment}>
                        New assessment
                    </Button>
                </Box>
            </Box>
            <Box display="flex" gap={3} flexWrap="wrap" alignItems="stretch">
                <Box flexBasis="34%" flexGrow={0} minWidth={300}>
                    <Paper className={classes.column}>
                        <Box className={classes.messages}>
                            {messages.map((m, i) => (
                                <Box key={i} className={m.role === "user" ? classes.bubbleUser : classes.bubbleAssistant}>
                                    {m.content ? (
                                        <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                                            {m.content}
                                        </Typography>
                                    ) : null}
                                    {m.questions && m.questions.length > 0 && (
                                        <ol style={{ margin: m.content ? "6px 0 0" : 0, paddingLeft: 20 }}>
                                            {m.questions.map((q, qi) => (
                                                <li key={qi}>
                                                    <Typography variant="body2">{q}</Typography>
                                                </li>
                                            ))}
                                        </ol>
                                    )}
                                </Box>
                            ))}
                            {sending && <CircularProgress size={20} />}
                            <div ref={endRef} />
                        </Box>
                        <Divider />
                        <Box className={classes.inputBar}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Describe the activity / task / equipment…"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                                disabled={sending}
                                multiline
                                maxRows={4}
                            />
                            <Button
                                variant="contained"
                                onClick={handleSend}
                                disabled={sending || !input.trim()}
                                endIcon={<SendIcon />}
                            >
                                Send
                            </Button>
                        </Box>
                    </Paper>
                </Box>

                <Box flexBasis="62%" flexGrow={1} minWidth={360}>
                    <Paper className={classes.column}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
                            <Typography variant="h6">Assessment preview</Typography>
                            <Box display="flex" gap={1}>
                                <Button
                                    variant="contained"
                                    startIcon={<SaveLibraryIcon />}
                                    onClick={handleSaveToLibrary}
                                    disabled={!assessment || saving}
                                >
                                    {saving ? "Saving…" : "Save to Library"}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<WordIcon />}
                                    onClick={handleDownload}
                                    disabled={generating || !assessment}
                                >
                                    {generating ? "Generating…" : "Download Word"}
                                </Button>
                            </Box>
                        </Box>
                        <Divider />
                        {error && (
                            <Alert severity="error" onClose={() => setError(null)} style={{ margin: 16 }}>
                                {error}
                            </Alert>
                        )}
                        {notice && (
                            <Alert severity="info" onClose={() => setNotice(null)} style={{ margin: 16 }}>
                                {notice}
                            </Alert>
                        )}
                        {!assessment ? (
                            <Box className={classes.previewEmpty}>
                                <Typography variant="body2">
                                    Your generated risk assessment will appear here, ready to review and download
                                    as a Word document in the WorkNest template.
                                </Typography>
                            </Box>
                        ) : (
                            <Box p={2} style={{ overflowY: "auto" }}>
                                <AssessmentPreview assessment={assessment} />
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};

export default RiskAssessmentGenerator;
